import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Star, MapPin, Heart, BadgeCheck } from "lucide-react";
import { catMap } from "@/lib/categories";
import { flagImg } from "@/lib/places";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

export function ListingCard({ listing, saved, onToggleSaved, hash }) {
  const nav = useNavigate();
  const { requireAuth } = useAuth();
  const [isSaved, setIsSaved] = useState(saved);
  const cat = catMap[listing.category] || catMap.sherbime;
  const img = (listing.images && listing.images[0]) || "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600";
  const flag = flagImg(listing.country);

  const toggle = (e) => {
    e.stopPropagation();
    requireAuth(async () => {
      try {
        const r = await api.post(`/favorites/${listing.id}`);
        setIsSaved(r.data.saved);
        onToggleSaved && onToggleSaved(listing.id, r.data.saved);
        toast.success(r.data.saved ? "U ruajt" : "U hoq nga të ruajturat");
      } catch { toast.error("Gabim"); }
    });
  };

  const priceText = listing.price != null ? `€${Number(listing.price).toLocaleString("de-DE")}${listing.price_unit || ""}` : null;

  return (
    <div
      data-testid={`listing-card-${listing.id}`}
      onClick={() => nav(`/shpallje/${listing.id}${hash ? `#${hash}` : ""}`)}
      className="bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer gs-fade"
    >
      <div className="relative">
        <img src={img} alt={listing.title} loading="lazy" decoding="async" className="w-full h-36 object-cover" />
        <span className={`absolute top-2 left-2 ${cat.solid} text-white text-xs font-semibold px-2.5 py-1 rounded-full shadow`}>{listing.subcategory || cat.label}</span>
        <button data-testid={`save-button-${listing.id}`} onClick={toggle} className="absolute top-2 right-2 bg-white/90 hover:bg-white rounded-full p-1.5 shadow">
          <Heart className={`w-4 h-4 ${isSaved ? "fill-red-500 text-red-500" : "text-slate-500"}`} />
        </button>
        {listing.sponsored && <span className="absolute bottom-2 left-2 bg-slate-900/80 text-white text-[10px] px-2 py-0.5 rounded-full">Sponsorizuar</span>}
        {flag && <img data-testid={`listing-flag-${listing.id}`} src={flag} alt={listing.country} title={listing.country} className="absolute bottom-2 right-2 w-5 h-3.5 object-cover rounded-[2px] shadow" />}
      </div>
      <div className="p-3">
        <div className="flex items-start justify-between gap-1">
          <h3 className="font-bold text-slate-800 text-sm leading-tight line-clamp-1">{listing.title}</h3>
          {listing.verified && <BadgeCheck className="w-4 h-4 text-emerald-500 shrink-0" />}
        </div>
        {priceText ? (
          <p className="text-emerald-600 font-extrabold text-base mt-1">{priceText}</p>
        ) : (
          <div className="flex items-center gap-1 mt-1">
            <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
            <span className="text-sm font-semibold text-slate-700">{listing.rating || "—"}</span>
            <span className="text-xs text-slate-400">({listing.review_count || 0})</span>
          </div>
        )}
        <div className="flex items-center justify-between gap-1 mt-1.5 text-xs text-slate-400">
          <span className="flex items-center gap-0.5 truncate min-w-0"><MapPin className="w-3 h-3 shrink-0" /><span className="truncate">{listing.city}</span></span>
          {listing.distance != null && <span className="font-medium text-slate-500 whitespace-nowrap shrink-0">{listing.distance} km</span>}
        </div>
      </div>
    </div>
  );
}

export default ListingCard;
